<template>
  <div class="bug-reporter">
    <!-- Bug Report Button -->
    <BugReportButton 
      v-if="currentStep === 'button'" 
      :button-text="buttonText"
      :button-style="buttonStyle"
      @open-modal="openModal"
    />

    <!-- Step 1: Bug Report Form -->
    <BugReportModal
      v-if="currentStep === 'form'"
      :is-capturing="isCapturing"
      :is-submitting="false"
      :console-log-count="consoleLogCount"
      :network-call-count="networkCallCount"
      :bug-report="bugReport"
      :show-screenshot-section="false"
      :has-existing-screenshot="!!capturedScreenshot"
      @close-modal="closeModal"
      @submit-report="proceedToScreenshot"
      @update-bug-report="updateBugReport"
    />

    <!-- Step 2: Screenshot Preview & Edit -->
    <ScreenshotReviewModal
      v-if="currentStep === 'screenshot'"
      :bug-report="bugReport"
      :captured-screenshot="capturedScreenshot"
      :final-screenshot="finalScreenshot"
      :is-capturing="isCapturing"
      :is-submitting="isSubmitting"
      @close-modal="closeModal"
      @back-to-form="backToForm"
      @edit-screenshot="openImageAnnotation"
      @submit-final-report="submitFinalReport"
      @recapture-screenshot="recaptureScreenshot"
    />

    <!-- Step 3: Image Annotation -->
    <ImageAnnotation
      v-if="currentStep === 'annotation'"
      :image-data="capturedScreenshot"
      :existing-annotations="screenshotAnnotations"
      @save="saveAnnotatedImage"
      @cancel="cancelImageAnnotation"
    />

    <!-- Success Message -->
    <SuccessMessage v-if="showSuccess" />
  </div>
</template>

<script>
import BugReportButton from './components/BugReportButton.vue'
import BugReportModal from './components/BugReportModal.vue'
import ScreenshotReviewModal from './components/ScreenshotReviewModal.vue'
import SuccessMessage from './components/SuccessMessage.vue'
import ImageAnnotation from './components/ImageAnnotation.vue'
import LogService from './services/logService.js'
import ReportService from './services/reportService.js'
import ScreenshotService from './services/screenshotService.js'

export default {
  name: 'BugReporter',
  
  components: {
    BugReportButton,
    BugReportModal,
    ScreenshotReviewModal,
    SuccessMessage,
    ImageAnnotation
  },
  
  props: {
    apiEndpoint: {
      type: String,
      default: process.env.VUE_APP_BUG_API + 'bug-reports/'
    },
    buttonText: {
      type: String,
      default: 'Report Bug'
    },
    userInfo: {
      type: Object,
      default: () => ({})
    }
  },
  
  data() {
    return {
      currentStep: 'button', // 'button', 'form', 'screenshot', 'annotation'
      isCapturing: false,
      isSubmitting: false,
      showSuccess: false,
      consoleLogCount: 0,
      networkCallCount: 0,
      capturedScreenshot: null,
      finalScreenshot: null,
      screenshotAnnotations: null,
      
      // Track existing modals before opening bug reporter
      existingVDialogs: [],
      
      bugReport: {
        description: '',
        steps: '',
        priority: 'medium',
      }
    }
  },
  
  computed: {
    buttonStyle() {
      return {
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        zIndex: 1000
      }
    }
  },
  
  mounted() {
    this.initializeServices()
    this.setupModalObserver()
  },
  
  beforeDestroy() {
    this.cleanupServices()
    this.cleanupModalObserver()
  },
  
  methods: {
    initializeServices() {
      // Initialize logging service
      LogService.initialize()
      LogService.onLogCountUpdate((count) => {
        this.consoleLogCount = count
      })
      LogService.onNetworkCallUpdate((count) => {
        this.networkCallCount = count
      })
    },
    
    cleanupServices() {
      LogService.cleanup()
    },

    // === MODAL CONFLICT FIXES ===
    
    /**
     * Setup observer to track existing modals
     */
    setupModalObserver() {
      // Watch for v-dialog elements being added/removed
      this.modalObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1 && this.isVDialog(node)) {
              this.trackExistingModal(node)
            }
          })
        })
      })

      this.modalObserver.observe(document.body, {
        childList: true,
        subtree: true
      })
    },

    /**
     * Cleanup modal observer
     */
    cleanupModalObserver() {
      if (this.modalObserver) {
        this.modalObserver.disconnect()
      }
    },

    /**
     * Check if element is a Vuetify dialog
     */
    isVDialog(element) {
      return element.classList && (
        element.classList.contains('v-dialog') ||
        element.classList.contains('v-overlay') ||
        element.classList.contains('v-overlay__content')
      )
    },

    /**
     * Track existing modal for later cleanup
     */
    trackExistingModal(element) {
      if (!this.existingVDialogs.includes(element)) {
        this.existingVDialogs.push(element)
      }
    },

    /**
     * Hide all existing modals except those that are currently visible
     */
    hideExistingModals() {
      this.existingVDialogs = []
      
      // Find all v-dialog related elements
      const selectors = [
        '.v-dialog',
        '.v-overlay',
        '.v-overlay__content',
        '.v-overlay__scrim',
        '[role="dialog"]'
      ]

      selectors.forEach(selector => {
        const elements = document.querySelectorAll(selector)
        elements.forEach(element => {
          // Skip bug reporter's own modals
          if (element.closest('.bug-reporter') || element.hasAttribute('data-bug-reporter')) {
            return
          }
          
          const isCurrentlyVisible = this.isElementVisible(element)
          
          if (element && element.style.display !== 'none') {
            this.existingVDialogs.push({
              element: element,
              originalDisplay: element.style.display,
              originalVisibility: element.style.visibility,
              originalZIndex: element.style.zIndex,
              wasVisible: isCurrentlyVisible,
              shouldPreserveForScreenshot: isCurrentlyVisible
            })
            
            if (!isCurrentlyVisible) {
              // Hide only non-visible elements
              element.style.display = 'none'
            } else {
              // Keep visible dialogs but adjust z-index to be below bug reporter
              element.style.zIndex = '10000'
              console.log('📌 Preserving visible dialog:', element.className)
            }
          }
        })
      })

      const hiddenCount = this.existingVDialogs.filter(d => !d.shouldPreserveForScreenshot).length
      const preservedCount = this.existingVDialogs.filter(d => d.shouldPreserveForScreenshot).length

      console.log(`🔒 Hidden ${hiddenCount} existing modals, preserved ${preservedCount} visible modals`)
    },

    /**
     * Check if element is currently visible to user
     */
    isElementVisible(element) {
      if (!element) return false
      
      const style = window.getComputedStyle(element)
      const rect = element.getBoundingClientRect()
      
      return style.display !== 'none' && 
             style.visibility !== 'hidden' && 
             style.opacity !== '0' &&
             rect.width > 0 && 
             rect.height > 0 &&
             rect.top < window.innerHeight &&
             rect.left < window.innerWidth
    },

    /**
     * Restore previously hidden modals
     */
    restoreExistingModals() {
      this.existingVDialogs.forEach(modalData => {
        if (modalData.element && modalData.element.parentNode) {
          // Restore original styles properly
          if (modalData.originalDisplay) {
            modalData.element.style.display = modalData.originalDisplay
          } else {
            modalData.element.style.removeProperty('display')
          }
          
          if (modalData.originalVisibility) {
            modalData.element.style.visibility = modalData.originalVisibility
          } else {
            modalData.element.style.removeProperty('visibility')
          }
          
          if (modalData.originalZIndex) {
            modalData.element.style.zIndex = modalData.originalZIndex
          } else {
            modalData.element.style.removeProperty('z-index')
          }
          
          // Force style recalculation
          modalData.element.offsetHeight
        }
      })

      console.log(`🔓 Restored ${this.existingVDialogs.length} existing modals`)
      
      // Clear the array and dispatch restoration event
      this.existingVDialogs = []
      
      // Give Vue a moment to reinitialize
      this.$nextTick(() => {
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('v-dialogs-restored'))
        }, 100)
      })
    },

    /**
     * Clean up any lingering v-dialog elements from DOM
     * Only affects hidden dialogs, preserves visible ones
     */
    cleanupDOMDialogs() {
      const selectors = [
        '.v-dialog:not([data-bug-reporter])',
        '.v-overlay:not([data-bug-reporter])',
        '.v-overlay__content:not([data-bug-reporter])',
        '.v-overlay__scrim:not([data-bug-reporter])'
      ]

      let hiddenCount = 0
      selectors.forEach(selector => {
        const elements = document.querySelectorAll(selector)
        elements.forEach(element => {
          const computedStyle = window.getComputedStyle(element)
          const isAlreadyHidden = computedStyle.display === 'none' || computedStyle.visibility === 'hidden'
          const isCurrentlyVisible = this.isElementVisible(element)
          
          // Only affect elements that are already hidden OR not currently visible
          if (isAlreadyHidden || !isCurrentlyVisible) {
            const hasNoContent = element.children.length === 0
            const hasNoVueInstance = !element.__vue__ && !element._vnode
            
            if (hasNoContent && hasNoVueInstance) {
              element.style.display = 'none'
              element.style.visibility = 'hidden'
              hiddenCount++
            } else if (isAlreadyHidden) {
              // Just ensure it stays hidden for screenshot
              element.style.display = 'none'
              hiddenCount++
            }
          }
          // Leave visible dialogs alone - they should be included in screenshot
        })
      })

      if (hiddenCount > 0) {
        console.log(`🧹 Ensured ${hiddenCount} hidden dialog elements stay hidden`)
      }
    },
    
    // Step Navigation Methods
    openModal() {
      // Hide existing modals before opening bug reporter
      this.hideExistingModals()
      
      this.currentStep = 'form'
      this.$emit('modal-opened')
    },
    
    closeModal() {
      this.currentStep = 'button'
      this.isCapturing = false
      this.isSubmitting = false
      this.capturedScreenshot = null
      this.finalScreenshot = null
      this.screenshotAnnotations = null
      this.resetForm()
      
      // Restore existing modals after closing bug reporter
      this.restoreExistingModals()
      
      this.$emit('modal-closed')
    },
    
    async proceedToScreenshot() {
      // Validate form first
      if (!this.bugReport.description.trim()) {
        if (this.$toast) {
          this.$toast.error('Please describe what happened')
        }
        return
      }
      
      // If we already have a screenshot, just proceed to step 2
      if (this.capturedScreenshot) {
        this.currentStep = 'screenshot'
        return
      }
      
      // Clean up DOM before capturing screenshot
      this.cleanupDOMDialogs()
      
      // Only capture screenshot if we don't have one
      this.isCapturing = true
      try {
        await this.captureScreenshot()
        this.currentStep = 'screenshot'
      } catch (error) {
        if (this.$toast) {
          this.$toast.error('Failed to capture screenshot. You can still submit without it.')
        }
        // Still proceed to screenshot step, but without image
        this.currentStep = 'screenshot'
      } finally {
        this.isCapturing = false
      }
    },
    
    backToForm() {
      this.currentStep = 'form'
    },
    
    openImageAnnotation() {
      if (!this.capturedScreenshot) {
        if (this.$toast) {
          this.$toast.error('No screenshot available to edit')
        }
        return
      }
      
      this.currentStep = 'annotation'
    },
    
    saveAnnotatedImage(annotationData) {
      // Store the annotated image and metadata
      this.finalScreenshot = annotationData.imageData
      this.screenshotAnnotations = {
        annotations: annotationData.annotations,
        blurAreas: annotationData.blurAreas
      }
      
      // Return to screenshot review
      this.currentStep = 'screenshot'
      
      if (this.$toast) {
        this.$toast.success('Screenshot annotations saved!')
      }
    },
    
    cancelImageAnnotation() {
      // Return to screenshot review without saving changes
      this.currentStep = 'screenshot'
    },
    
    async recaptureScreenshot() {
      this.isCapturing = true
      try {
        // Clear existing screenshots
        this.capturedScreenshot = null
        this.finalScreenshot = null
        this.screenshotAnnotations = null
        
        // Clean up DOM before capturing
        this.cleanupDOMDialogs()
        
        // Capture new screenshot
        await this.captureScreenshot()
        
        if (this.$toast) {
          this.$toast.success('New screenshot captured!')
        }
      } catch (error) {
        if (this.$toast) {
          this.$toast.error('Failed to capture new screenshot')
        }
      } finally {
        this.isCapturing = false
      }
    },
    
    // Force new screenshot capture (for when user wants to retake)
    async forceNewScreenshot() {
      // Clear existing screenshot first to force recapture
      this.capturedScreenshot = null
      this.finalScreenshot = null
      this.screenshotAnnotations = null
      
      // Now proceed to screenshot step (will capture new one)
      await this.proceedToScreenshot()
    },
    
    // Form Methods
    resetForm() {
      this.bugReport = {
        description: '',
        steps: '',
        priority: 'medium',
      }
    },
    
    updateBugReport(field, value) {
      this.bugReport[field] = value
    },
    
    // Screenshot Methods
    async captureScreenshot() {
      try {
        // Wait a moment for any DOM changes to settle
        await new Promise(resolve => setTimeout(resolve, 300))
        
        const screenshot = await ScreenshotService.captureScreenshot()
        
        if (screenshot && screenshot.data) {
          this.capturedScreenshot = screenshot.data
        } else {
          this.capturedScreenshot = null
        }
      } catch (error) {
        this.capturedScreenshot = null
        throw error
      }
    },
    
    // Final Submission
    async submitFinalReport() {
      try {
        this.isSubmitting = true
        
        // Collect all bug data using services
        const bugData = await this.collectBugData()
        
        // Send report using ReportService
        const response = await ReportService.submitReport({
          apiEndpoint: this.apiEndpoint,
          userInfo: this.userInfo,
          bugReport: this.bugReport,
          bugData
        })
        
        // Success handling
        this.handleSubmitSuccess(response)
        
      } catch (error) {
        this.handleSubmitError(error)
      } finally {
        this.isSubmitting = false
      }
    },
    
    async collectBugData() {
      const debugData = LogService.getDebugData()
      
      // Use final annotated screenshot if available, otherwise use original
      const screenshotToUse = this.finalScreenshot || this.capturedScreenshot
      
      return {
        context: this.getContextData(),
        debugData,
        attachments: screenshotToUse ? [{
          type: 'screenshot',
          name: 'screenshot.jpg',
          data: screenshotToUse,
          size: Math.round(screenshotToUse.length / 1024),
        }] : []
      }
    },
    
    getContextData() {
      return {
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        browserInfo: this.getBrowserInfo(),
        pageInfo: {
          title: document.title,
          viewport: {
            width: window.innerWidth,
            height: window.innerHeight
          },
          route: this.$route ? this.$route.path : window.location.pathname
        }
      }
    },
    
    getBrowserInfo() {
      return {
        userAgent: navigator.userAgent,
        language: navigator.language,
        platform: navigator.platform,
        cookieEnabled: navigator.cookieEnabled,
        onLine: navigator.onLine,
        screen: {
          width: screen.width,
          height: screen.height
        },
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        }
      }
    },
    
    // Success/Error Handlers
    handleSubmitSuccess(response) {
      this.currentStep = 'button'
      this.showSuccessMessage()
      this.resetForm()
      
      // Clear screenshot data
      this.capturedScreenshot = null
      this.finalScreenshot = null
      this.screenshotAnnotations = null
      
      // Restore existing modals
      this.restoreExistingModals()
      
      this.$emit('bug-reported', response.data)
      
      if (this.$toast) {
        this.$toast.success('Bug report sent successfully!')
      }
    },
    
    handleSubmitError(error) {
      this.$emit('error', error)
      
      if (this.$toast) {
        this.$toast.error('Failed to send bug report. Please try again.')
      }
    },
    
    showSuccessMessage() {
      this.showSuccess = true
      setTimeout(() => {
        this.showSuccess = false
      }, 3000)
    }
  }
}
</script>

<style scoped>
.bug-reporter {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* Ensure proper z-index layering - Higher than Vuetify modals */
.bug-reporter >>> .bug-modal-overlay {
  z-index: 15000 !important; /* Higher than Vuetify's default 1000-2000 */
}

.bug-reporter >>> .image-annotation-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 15001 !important; /* Even higher for annotation tool */
  background: white;
}

/* Mark bug reporter modals to avoid cleanup */
.bug-reporter >>> .v-overlay,
.bug-reporter >>> .v-dialog {
  z-index: 15000 !important;
}

.bug-reporter >>> .v-overlay::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: -1;
}
</style>